import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Plane, 
  Clock, 
  MapPin, 
  Calendar,
  Shield,
  Star,
  ChevronRight,
  ChevronLeft,
  ArrowRight,
  Users,
  Award,
  Phone
} from 'lucide-react';

// User provided images
const IMAGES = {
  // Hero slideshow images
  heroCapitol: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/7mbofut4_1.jpg',
  heroWaldorf: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/pxgayyb3_4.jpg',
  heroMonument: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg',
  
  // Fleet images
  fleetLineup: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/pxgayyb3_4.jpg',
  fleetWaterview: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/1mru6jql_2.jpg',
  
  // Event images
  eventPurple: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/cuc2w3b4_3.jpg',
  eventWaldorf: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/pxgayyb3_4.jpg',
  
  // Other user images
  escaladeExterior: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/zdycqsl8_IMG_9182.jpg',
  escaladeInterior: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/fztrwjm3_my26-escalade-gallery-interior-modal-steering-wheel-v2.jpeg',
  
  // Airport images
  airportWaterview: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/1mru6jql_2.jpg',
  iad: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/yfbsvpaf_iad%20airport%20dulles.jpg',
  dca: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/nbgab8mz_dca-tower.jpeg',
  terminal: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/19rc71yl_1.jpg',
  
  // DC images
  dcMonument: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/t8s41v2p_2.jpg',
  capitol: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/7mbofut4_1.jpg',
  
  // Government/Executive
  whiteHouse: 'https://images.unsplash.com/photo-1570492903043-3d30df7133d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxXYXNoaW5ndG9uJTIwREMlMjBXaGl0ZSUyMEhvdXNlJTIwZ292ZXJubWVudCUyMGV4ZWN1dGl2ZXxlbnwwfHx8fDE3NzM2OTA5NDB8MA&ixlib=rb-4.1.0&q=85',
};

// Hero slideshow images
const heroSlides = [
  {
    image: IMAGES.heroCapitol,
    alt: 'Cadillac Escalade at US Capitol Building'
  },
  {
    image: IMAGES.heroWaldorf,
    alt: 'Fleet of Escalades at Waldorf Astoria'
  },
  {
    image: IMAGES.heroMonument,
    alt: 'Escalade at Washington Monument'
  }
];

const services = [
  {
    icon: Plane,
    title: 'Airport Transfers',
    description: 'Reliable luxury transportation to and from major airports in the Washington DC region with professional chauffeurs and flight tracking.',
    link: '/services#airport-transfers'
  },
  {
    icon: Clock,
    title: 'Hourly Chauffeur Service',
    description: 'Flexible luxury transportation with a private driver available for meetings, city travel, or personal schedules.',
    link: '/services#hourly-service'
  },
  {
    icon: MapPin,
    title: 'City to City Transportation',
    description: 'Comfortable long-distance travel between major cities without the stress of flights or rental cars.',
    link: '/services#city-to-city'
  },
  {
    icon: Calendar,
    title: 'Event Transportation',
    description: 'Professional chauffeur service for weddings, corporate events, and special occasions.',
    link: '/services#event-transportation'
  }
];

const airports = [
  {
    code: 'DCA',
    name: 'Reagan National',
    fullName: 'Ronald Reagan Washington National Airport',
    distance: '15 min from Downtown DC'
  },
  {
    code: 'IAD',
    name: 'Dulles International',
    fullName: 'Washington Dulles International Airport',
    distance: '45 min from Downtown DC'
  },
  {
    code: 'BWI',
    name: 'Baltimore/Washington',
    fullName: 'Baltimore/Washington International Airport',
    distance: '50 min from Downtown DC'
  }
];

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-rotate slideshow
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // 5 seconds

    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section with Slideshow */}
      <section className="hero-section relative min-h-screen flex items-center overflow-hidden">
        {/* Slideshow Images */}
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/95 via-[#050505]/70 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/30"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32 lg:py-0">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="fade-in inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-8">
              <Shield className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs text-[#D4AF37] tracking-wider uppercase">
                Licensed & Insured • Washington DC
              </span>
            </div>
            
            {/* Headline */}
            <h1 
              data-testid="hero-headline"
              className="fade-in-delay-1 text-hero font-serif text-white mb-6"
            >
              Executive Chauffeur Service in{' '}
              <span className="text-[#D4AF37]">Washington DC</span>
            </h1>
            
            {/* Subtext */}
            <p className="fade-in-delay-2 text-lg lg:text-xl text-[#A3A3A3] mb-10 leading-relaxed max-w-2xl">
              Premium black car transportation for government officials, corporate executives, 
              and distinguished travelers. Experience first-class service with our luxury 
              Cadillac Escalade fleet.
            </p>
            
            {/* CTA Buttons */}
            <div className="fade-in-delay-3 flex flex-wrap gap-4">
              <Link to="/book">
                <Button 
                  data-testid="hero-book-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase"
                >
                  Book a Ride
                </Button>
              </Link>
              <a href="tel:+12026840534">
                <Button 
                  data-testid="hero-call-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase inline-flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  (202) 684-0534
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* Slideshow Navigation Arrows */}
        <button 
          onClick={prevSlide}
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-black/30 hover:bg-[#D4AF37]/20 border border-white/10 hover:border-[#D4AF37]/50 transition-all"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <button 
          onClick={nextSlide}
          className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-black/30 hover:bg-[#D4AF37]/20 border border-white/10 hover:border-[#D4AF37]/50 transition-all"
          aria-label="Next slide"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        {/* Slideshow Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentSlide 
                  ? 'w-8 bg-[#D4AF37]' 
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Luxury Fleet Section - Updated with new image */}
      <section className="section-padding bg-[#0A0A0A] relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="mb-12 text-center">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              Our Fleet
            </span>
            <h2 className="text-section font-serif text-white mb-6">
              Luxury Fleet at Your Service
            </h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto text-lg leading-relaxed">
              Experience unparalleled comfort with our meticulously maintained fleet of 
              Cadillac Escalades and executive SUVs.
            </p>
          </div>

          {/* Fleet Lineup Image - Full Width - NEW IMAGE */}
          <div className="relative mb-12">
            <div className="aspect-[21/9] overflow-hidden">
              <img
                src={IMAGES.fleetLineup}
                alt="Capital Chauffeur Services Fleet - Multiple Black Cadillac Escalades at Waldorf Astoria"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
          </div>

          {/* Vehicle Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-8 bg-[#050505] border border-white/5">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-[#D4AF37]/30">
                <Users className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl text-white mb-2">Up to 6 Passengers</h3>
              <p className="text-[#A3A3A3] text-sm">Spacious seating for groups</p>
            </div>
            <div className="text-center p-8 bg-[#050505] border border-white/5">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-[#D4AF37]/30">
                <Star className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl text-white mb-2">Premium Amenities</h3>
              <p className="text-[#A3A3A3] text-sm">WiFi, charging, refreshments</p>
            </div>
            <div className="text-center p-8 bg-[#050505] border border-white/5">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border border-[#D4AF37]/30">
                <Award className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-serif text-xl text-white mb-2">Private Executives Cabin</h3>
              <p className="text-[#A3A3A3] text-sm">Discreet, comfortable interior designed for business and leisure travel</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="section-padding bg-[#050505] relative">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              Our Services
            </span>
            <h2 className="text-section font-serif text-white mb-6">
              Premium Transportation Solutions
            </h2>
            <p className="text-[#A3A3A3] max-w-2xl text-lg leading-relaxed">
              Experience the finest in luxury ground transportation with our 
              comprehensive range of services tailored to your needs.
            </p>
          </div>

          {/* Services Grid - Bento Style */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <Link 
                key={service.title} 
                to={service.link}
                data-testid={`service-card-${index}`}
                className="service-card p-8 lg:p-10 group cursor-pointer transition-all duration-500"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/10 transition-colors">
                    <service.icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-serif text-xl lg:text-2xl text-white mb-3 group-hover:text-[#D4AF37] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-[#A3A3A3] leading-relaxed mb-4">
                      {service.description}
                    </p>
                    <span className="inline-flex items-center gap-2 text-[#D4AF37] text-sm group-hover:gap-4 transition-all">
                      Learn More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Detail Section */}
      <section className="section-padding bg-[#0A0A0A] relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image Side - Updated with fleet image */}
            <div className="relative order-2 lg:order-1">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.fleetWaterview}
                  alt="Capital Chauffeur Services Fleet with Professional Chauffeur"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
              </div>
              {/* Floating Interior Image */}
              <div className="absolute -bottom-8 -right-4 lg:right-8 w-48 lg:w-64 aspect-[4/3] border-4 border-[#0A0A0A] shadow-2xl">
                <img
                  src={IMAGES.escaladeInterior}
                  alt="Cadillac Escalade Luxury Interior"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Content Side */}
            <div className="order-1 lg:order-2">
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
                Cadillac Escalade
              </span>
              <h2 className="text-section font-serif text-white mb-6">
                The Ultimate in Executive Luxury
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-8">
                Our flagship Cadillac Escalade ESV offers the perfect blend of power, 
                sophistication, and comfort. Featuring premium leather interiors, 
                advanced climate control, and state-of-the-art entertainment systems.
              </p>

              <div className="space-y-4 mb-10">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">Extended Wheelbase for Maximum Legroom</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">Premium Leather Seating</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">Privacy Tinted Windows</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">Complimentary WiFi & Charging</span>
                </div>
              </div>

              <Link to="/book">
                <Button 
                  data-testid="fleet-book-btn"
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase"
                >
                  Reserve Your Vehicle
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Airport Coverage Section - IMPROVED */}
      <section id="airport-section" data-testid="airports-section" className="section-padding bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="mb-12 text-center">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              Airport Transportation
            </span>
            <h2 className="text-section font-serif text-white mb-6">
              Serving All Major DC Airports
            </h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto text-lg leading-relaxed">
              Professional airport transportation with real-time flight monitoring, 
              meet and greet service, and complimentary wait time.
            </p>
          </div>

          {/* Airport Cards - Prominent Display */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {airports.map((airport) => (
              <div 
                key={airport.code}
                className="bg-[#0A0A0A] border border-white/10 p-8 text-center hover:border-[#D4AF37]/50 transition-colors group"
              >
                <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-[#D4AF37] text-black">
                  <span className="text-2xl font-bold">{airport.code}</span>
                </div>
                <h3 className="font-serif text-xl text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
                  {airport.name}
                </h3>
                <p className="text-[#A3A3A3] text-sm mb-4">
                  {airport.fullName}
                </p>
                <p className="text-[#D4AF37] text-sm">
                  {airport.distance}
                </p>
              </div>
            ))}
          </div>

          {/* Airport Images - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.airportWaterview}
                alt="Escalades with Professional Chauffeur at Building Entrance"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img
                src={IMAGES.iad}
                alt="Washington Dulles International Airport"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link to="/book">
              <Button 
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Book Airport Transfer
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Event Transportation Section - Updated with new images */}
      <section id="event-section" className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content Side */}
            <div>
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
                Event Transportation
              </span>
              <h2 className="text-section font-serif text-white mb-6">
                Make Every Occasion Memorable
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-8">
                From corporate galas to weddings, our luxury fleet ensures you arrive 
                in style. Professional chauffeurs, impeccable vehicles, and flawless 
                coordination for your special events.
              </p>

              <div className="space-y-4 mb-10">
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">Corporate Events & Galas</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">Wedding Transportation</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">VIP & Diplomatic Services</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-2 h-2 bg-[#D4AF37]"></div>
                  <span className="text-white">Multi-Vehicle Coordination</span>
                </div>
              </div>

              <Link to="/book">
                <Button 
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase"
                >
                  Book Event Transportation
                </Button>
              </Link>
            </div>

            {/* Image Grid - NEW EVENT IMAGES */}
            <div className="grid grid-cols-1 gap-4">
              <div className="aspect-video overflow-hidden">
                <img
                  src={IMAGES.eventWaldorf}
                  alt="Six Cadillac Escalades Lined Up at Waldorf Astoria"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-video overflow-hidden">
                <img
                  src={IMAGES.eventPurple}
                  alt="Cadillac Escalade at Howard Theatre Event"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DC Government Section */}
      <section className="section-padding bg-[#050505] relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content Side */}
            <div>
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
                Trusted by Leaders
              </span>
              <h2 className="text-section font-serif text-white mb-6">
                Executive Transportation for the Nation&apos;s Capital
              </h2>
              <p className="text-[#A3A3A3] text-lg leading-relaxed mb-8">
                Capital Chauffeur Services provides premium transportation for government 
                officials, diplomats, corporate executives, and distinguished guests 
                throughout the Washington DC metropolitan area.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-10">
                <div className="p-4 bg-[#0A0A0A] border border-white/5">
                  <div className="text-3xl font-serif text-[#D4AF37] mb-2">24/7</div>
                  <p className="text-[#A3A3A3] text-sm">Availability</p>
                </div>
                <div className="p-4 bg-[#0A0A0A] border border-white/5">
                  <div className="text-3xl font-serif text-[#D4AF37] mb-2">100%</div>
                  <p className="text-[#A3A3A3] text-sm">Discretion</p>
                </div>
              </div>

              <Link to="/about">
                <Button 
                  variant="outline"
                  className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase"
                >
                  Learn About Us
                </Button>
              </Link>
            </div>

            {/* Image Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="aspect-square overflow-hidden">
                <img
                  src={IMAGES.capitol}
                  alt="Escalade at US Capitol"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square overflow-hidden mt-8">
                <img
                  src={IMAGES.whiteHouse}
                  alt="The White House"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square overflow-hidden">
                <img
                  src={IMAGES.dcMonument}
                  alt="Washington Monument"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-square overflow-hidden mt-8">
                <img
                  src={IMAGES.heroMonument}
                  alt="Escalade at Washington Monument"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#0A0A0A] relative overflow-hidden">
        {/* Background Texture */}
        <div className="absolute inset-0">
          <img
            src={IMAGES.fleetLineup}
            alt=""
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/80 to-[#0A0A0A]"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-section font-serif text-white mb-6">
            Ready to Experience{' '}
            <span className="text-[#D4AF37]">Executive Travel</span>?
          </h2>
          <p className="text-[#A3A3A3] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Book your next journey with Capital Chauffeur Services and discover 
            why Washington DC&apos;s executives and government officials trust us for their 
            transportation needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button 
                data-testid="cta-book-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Book Your Ride Now
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Call (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
